<script>
  import { onMount, onDestroy } from "svelte";
  import CardStatus from "../components/Cards/CardStatus.svelte";
  import {
    fetchEgresoCabs,
    subscribeToEgresoCabs,
    unsubscribeFromEgresoCabs,
  } from "../controllers/egresoCabController.js";
  import { DASHBOARD_CONFIG } from "../config.js";
  import { updateEgresoCab, deleteEscoltasFromEgreso, addEscoltasToEgreso } from "../models/egresoCabModel";
  import { fetchSucursales } from "../controllers/sucursalController.js";

  let egresoCabData = [];
  let filteredData = [];
  let isLoading = true;
  let error = null;
  let searchQuery = "";
  let showMobileFilters = false;
  let sucursalOptions = [];
  let selectedSucursal = "";
  
  // Get today's date for max value of date inputs
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const todayStr = today.toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' }); // Format YYYY-MM-DD

  // Calculate yesterday's date
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const yesterdayStr = yesterday.toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' }); // Format YYYY-MM-DD

  // Set default date range filter variables
  let startDate = yesterdayStr; // Default to yesterday
  let endDate = todayStr; // Default to today

  // New variable for delivery method filter
  let selectedDeliveryMethod = "";

  // Load sucursales on mount
  async function loadSucursales() {
    try {
      const fetchedSucursales = await fetchSucursales();
      if (Array.isArray(fetchedSucursales.sucursal)) {
        sucursalOptions = fetchedSucursales.sucursal.map(e => ({
          key: e.id,
          value: e.descrip
        }));
      }
    } catch (error) {
      console.error('Error al cargar las sucursales:', error);
    }
  }

  const fetchData = async () => {
    isLoading = true;
    error = null;
    try {
      const result = await fetchEgresoCabs(1, 999999);
      if (result) {
        egresoCabData = result.egresoCab;
        filterData();
      }
    } catch (err) {
      error = "Error al cargar los datos. Inténtelo de nuevo más tarde.";
      console.error("Error fetching data:", err);
    } finally {
      isLoading = false;
    }
  };

  const groupByStatus = (data) => {
    const grouped = {};
    
    // Initialize all configured statuses
    DASHBOARD_CONFIG.displayStatuses.forEach(status => {
      grouped[status] = [];
    });

    data.forEach((item) => {
      if (grouped[item.sta]) {
        grouped[item.sta].push(item);
      }
    });

    return grouped;
  };

  $: groupedData = groupByStatus(filteredData);
  $: visibleStatuses = DASHBOARD_CONFIG.displayStatuses;

  const handleSearch = async (page) => {
    const result = await fetchEgresoCabs(page, 1000);
    if (result) {
      egresoCabData = result.egresoCab;
      filterData();
    }
  };

  const filterData = () => {
    filteredData = egresoCabData.filter((item) => {
      // Create a date from the item's date field (assuming created field is used)
      const itemDate = new Date(item.created);
      
      // Check if item matches text search query
      const matchesTextSearch = !searchQuery || 
        item.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.numero.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.nit.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.uuid.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Check if item is within date range
      const matchesStartDate = !startDate || itemDate >= new Date(startDate);
      const matchesEndDate = !endDate || itemDate <= new Date(endDate + 'T23:59:59');
      
      // Check if item matches selected delivery method
      const matchesDeliveryMethod = !selectedDeliveryMethod || item.delivery_method === selectedDeliveryMethod;
      
      // Check if item matches selected sucursal
      const matchesSucursal = !selectedSucursal || item.sucursal === selectedSucursal;
      
      return matchesTextSearch && matchesStartDate && matchesEndDate && matchesDeliveryMethod && matchesSucursal;
    });
  };

  const handleSearchInput = (event) => {
    searchQuery = event.target.value;
    filterData();
  };
  
  // Add handlers for date filter changes
  const handleStartDateChange = (event) => {
    startDate = event.target.value;
    filterData();
  };
  
  const handleEndDateChange = (event) => {
    endDate = event.target.value;
    filterData();
  };

  // Handler for delivery method change
  const handleDeliveryMethodChange = (event) => {
    selectedDeliveryMethod = event.target.value;
    filterData();
  };

  // Handler for sucursal change
  const handleSucursalChange = (event) => {
    selectedSucursal = event.target.value;
    filterData();
  };
  
  // Clear all filters
  const clearFilters = () => {
    searchQuery = "";
    startDate = "";
    endDate = "";
    selectedDeliveryMethod = "";
    selectedSucursal = "";
    filterData();
  };

  async function handleLocationUpdate(event) {
    console.log("Event payload in Dashboard:", event.detail);
    const { id, geo_target_ref, geo_delivery_ref } = event.detail;

    try {
      // Update in PocketBase
      await updateEgresoCab(id, {
        ...(geo_target_ref && { geo_target_ref }), // Only include if geo_target_ref exists
        ...(geo_delivery_ref && { geo_delivery_ref }), // Only include if geo_delivery_ref exists
      });

      // Update the local state (egresoCabData)
      egresoCabData = egresoCabData.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            ...(geo_target_ref && { geo_target_ref }), // Update geo_target_ref if it exists
            ...(geo_delivery_ref && { geo_delivery_ref }), // Update geo_delivery_ref if it exists
          };
        }
        return item;
      });

      // Update filteredData to reflect the changes in the UI
      filterData();
    } catch (err) {
      console.error("Error updating location:", err);
      error = "Error al actualizar la ubicación. Inténtelo de nuevo más tarde.";
    }
  }

  async function handleDeliveryUpdate(event) {
    console.log("Event payload in Dashboard (Delivery Update):", event.detail);
    const { id, delivery_method, delivery_ref, delivery, transporte, transporte_ref, target_ref, vehiculo } = event.detail;

    try {
      // Update in PocketBase
      await updateEgresoCab(id, {
        ...(delivery_method && { delivery_method }), // Only include if delivery_method exists
        ...(delivery_ref && { delivery_ref }), // Only include if delivery_ref exists
        ...(delivery && { delivery }), // Only include if delivery exists
        ...(transporte && { transporte }), // Only include if transporte exists
        ...(transporte_ref && { transporte_ref }), // Only include if transporte_ref exists
        ...(target_ref && { target_ref }), // Only include if target_ref exists
        ...(vehiculo && { vehiculo }), // Only include if vehiculo exists
      });

      // Update the local state (egresoCabData)
      egresoCabData = egresoCabData.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            ...(delivery_method && { delivery_method }), // Update delivery_method if it exists
            ...(delivery_ref && { delivery_ref }), // Update delivery_ref if it exists
            ...(delivery && { delivery }), // Update delivery if it exists
            ...(transporte && { transporte }), // Update transporte if it exists
            ...(transporte_ref && { transporte_ref }), // Update transporte_ref if it exists
            ...(target_ref && { target_ref }), // Update target_ref if it exists
            ...(vehiculo && { vehiculo }), // Update vehiculo if it exists
          };
        }
        return item;
      });

      // Update filteredData to reflect the changes in the UI
      filterData();
    } catch (err) {
      console.error("Error updating delivery:", err);
      error = "Error al actualizar la entrega. Inténtelo de nuevo más tarde.";
    }
  }

  async function handleEgresoEscoltaUpdate(event) {
    console.log("Event payload in Dashboard (Escoltas Update):", event.detail);
    const { egresoId, escoltas } = event.detail;

    try {
      // Step 1: Delete any existing escoltas for this egreso
      await deleteEscoltasFromEgreso(egresoId);

      // Step 2: Add new escoltas from the payload
      if (escoltas && escoltas.length > 0) {
        await addEscoltasToEgreso(egresoId, escoltas);
      }

      // Step 3: Update the local state to include escoltas information
      egresoCabData = egresoCabData.map((item) => {
        if (item.id === egresoId) {
          return {
            ...item,
            escoltas: escoltas || [] // Update escoltas for the matching egreso
          };
        }
        return item;
      });

      // Step 4: Update filteredData to reflect the changes in the UI
      filterData();
    } catch (err) {
      console.error("Error updating escoltas:", err);
      error = "Error al actualizar las escoltas. Inténtelo de nuevo más tarde.";
    }
  }

  onMount(() => {
    fetchData();
    loadSucursales();
    subscribeToEgresoCabs(handleSearch);
  });

  onDestroy(() => {
    unsubscribeFromEgresoCabs();
  });
</script>

<div class="relative md:pt-6 pb-4 flex flex-col md:flex-row justify-between items-start md:items-center">
  <h2 class="text-white text-lg font-bold mb-4 md:mb-0">Estado de Pedidos</h2>
  
  <!-- Mobile Filter Button -->
  <button
    class="md:hidden px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 mb-4 flex items-center gap-2 shadow-md"
    on:click={() => showMobileFilters = true}
  >
    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clip-rule="evenodd" />
    </svg>
    Filtros
  </button>

  <!-- Desktop Filters -->
  <div class="hidden md:flex flex-col md:flex-row items-start md:items-center w-full md:w-auto gap-4">
    <div class="flex flex-col md:flex-row items-start md:items-center gap-4">
      <div class="flex items-center gap-2">
        <span class="text-white">Desde:</span>
        <input
          type="date"
          bind:value={startDate}
          on:change={handleStartDateChange}
          max={endDate || todayStr}
          class="px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div class="flex items-center gap-2">
        <span class="text-white">Hasta:</span>
        <input
          type="date"
          bind:value={endDate}
          on:change={handleEndDateChange}
          min={startDate}
          max={todayStr}
          class="px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div class="flex items-center gap-2">
        <span class="text-white">Método:</span>
        <select
          bind:value={selectedDeliveryMethod}
          on:change={handleDeliveryMethodChange}
          class="px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos</option>
          <option value="Despacho">Despacho</option>
          <option value="Repartidor">Repartidor</option>
          <option value="Transporte">Transporte</option>
        </select>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-white">Sucursal:</span>
        <select
          bind:value={selectedSucursal}
          on:change={handleSucursalChange}
          class="px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todas</option>
          {#each sucursalOptions as option}
            <option value={option.key}>{option.value}</option>
          {/each}
        </select>
      </div>
    </div>
    <div class="flex items-center gap-2">
      <input
        type="text"
        bind:value={searchQuery}
        on:input={handleSearchInput}
        placeholder="Buscar ..."
        class="px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button 
        on:click={clearFilters}
        class="px-4 py-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 focus:outline-none"
      >
        Limpiar
      </button>
    </div>
  </div>

  <!-- Mobile Search -->
  <div class="md:hidden flex items-center gap-2 w-full">
    <input
      type="text"
      bind:value={searchQuery}
      on:input={handleSearchInput}
      placeholder="Buscar ..."
      class="flex-1 px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
    <button 
      on:click={clearFilters}
      class="px-4 py-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 focus:outline-none"
    >
      Limpiar
    </button>
  </div>
</div>

<!-- Mobile Filter Modal -->
{#if showMobileFilters}
  <div class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div class="bg-white rounded-lg p-6 w-full max-w-md">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold">Filtros</h3>
        <button 
          on:click={() => showMobileFilters = false}
          class="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>
      
      <div class="space-y-4">
        <div class="flex flex-col gap-2">
          <span class="text-gray-700">Desde:</span>
          <input
            type="date"
            bind:value={startDate}
            on:change={handleStartDateChange}
            max={endDate || todayStr}
            class="w-full px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div class="flex flex-col gap-2">
          <span class="text-gray-700">Hasta:</span>
          <input
            type="date"
            bind:value={endDate}
            on:change={handleEndDateChange}
            min={startDate}
            max={todayStr}
            class="w-full px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div class="flex flex-col gap-2">
          <span class="text-gray-700">Método de entrega:</span>
          <select
            bind:value={selectedDeliveryMethod}
            on:change={handleDeliveryMethodChange}
            class="w-full px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos</option>
            <option value="Despacho">Despacho</option>
            <option value="Repartidor">Repartidor</option>
            <option value="Transporte">Transporte</option>
          </select>
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-gray-700">Sucursal:</span>
          <select
            bind:value={selectedSucursal}
            on:change={handleSucursalChange}
            class="w-full px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todas</option>
            {#each sucursalOptions as option}
              <option value={option.key}>{option.value}</option>
            {/each}
          </select>
        </div>
        
        <div class="flex justify-end gap-2 mt-4">
          <button 
            on:click={clearFilters}
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none"
          >
            Limpiar
          </button>
          <button 
            on:click={() => showMobileFilters = false}
            class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<div class="p-4">
  {#if isLoading}
    <p class="text-blueGray-500">Cargando datos...</p>
  {:else if error}
    <p class="text-red-500">{error}</p>
  {:else}
    {#each Array(Math.ceil(visibleStatuses.length / 3)) as _, rowIndex}
      <div class="flex flex-wrap {rowIndex > 0 ? 'mt-4' : ''}">
        {#each visibleStatuses.slice(rowIndex * 3, (rowIndex + 1) * 3) as status}
          <div class="w-full xl:w-4/12 mb-12 xl:mb-0 px-4">
            <CardStatus
              title={status === 'NOT_READY' ? 'No Listo' :
                     status === 'PACKING' ? 'En Empaque' :
                     status === 'PARTIALLY_READY' ? 'Parcialmente Listo' :
                     status === 'READY' ? 'Listo' :
                     status === 'ON_ROUTE' ? 'En Ruta' :
                     status === 'DELIVERED' ? 'Entregado' :
                     'Devuelto'}
              status={status}
              items={groupedData[status]}
              on:updateLocation={handleLocationUpdate}
              on:updateDelivery={handleDeliveryUpdate}
              on:updateEgresoEscolta={status === 'READY' ? handleEgresoEscoltaUpdate : undefined}
            />
          </div>
        {/each}
      </div>
    {/each}
  {/if}
</div>